import { useCallback, useEffect, useState } from 'react'

import {
  hasPageImage,
  pageImageSrc,
  requestPageImages,
  subscribeToPageImages,
} from './pageImages'
import type { DocumentPage, PageSource } from './types'

export interface PageImages {
  isLoaded: (page: number) => boolean
  request: (pages: number[]) => void
  srcFor: (page: number) => string | undefined
}

// The page number is the dense 1-based ordinal over this same list, so a page sits at its own
// index. The scan is only a guard for a caller that ever hands over a sparser list.
const sourceOf = (
  pages: DocumentPage[],
  number: number,
): PageSource | undefined => {
  const atIndex = pages[number - 1]
  if (atIndex?.number === number) return atIndex.source
  return pages.find((page) => page.number === number)?.source
}

// The images live in a module-level cache (pageImages.ts) that survives a remount.
export const usePageImages = (pages: DocumentPage[]): PageImages => {
  const [, redraw] = useState(0)

  useEffect(() => subscribeToPageImages(() => redraw((tick) => tick + 1)), [])

  const isLoaded = useCallback(
    (page: number) => {
      const source = sourceOf(pages, page)
      return source !== undefined && hasPageImage(source)
    },
    [pages],
  )

  const srcFor = useCallback(
    (page: number) => {
      const source = sourceOf(pages, page)
      return source && pageImageSrc(source)
    },
    [pages],
  )

  const request = useCallback(
    (wanted: number[]) => {
      requestPageImages(
        wanted
          .map((page) => sourceOf(pages, page))
          .filter((source) => source !== undefined),
      )
    },
    [pages],
  )

  return { isLoaded, request, srcFor }
}
